import { DecodingSDK, } from "@blockdaemon/transaction-verifier";
import { getLogger } from "../../shared/logger";
import { isSecurityModuleParameterMismatch, throwSecurityModuleMismatchError, } from "./security-errors";
import { SecurityModuleSolana } from "./solana/security-module-solana";
import { SecurityModuleStakewise } from "./stakewise/security-module-stakewise";
/**
 * Checks if a chain ID represents an EVM-compatible chain.
 * EVM chains use the CAIP-2 format "eip155:{chainId}".
 */
function isEvmChain(chainId) {
    return typeof chainId === "string" && chainId.startsWith("eip155:");
}
/**
 * Checks if a chain ID represents a Solana chain.
 * Solana chains use the CAIP-2 format "solana:{network}".
 */
function isSolanaChain(chainId) {
    return typeof chainId === "string" && chainId.startsWith("solana:");
}
/**
 * Extracts chainId from a request object.
 */
function getChainId(request) {
    if (request && typeof request === "object" && "chainId" in request) {
        const chainId = request.chainId;
        return typeof chainId === "string" ? chainId : undefined;
    }
    return undefined;
}
/**
 * Checks if an operation is a Solana-specific operation.
 * Solana operations are prefixed with "solana_" (e.g., "solana_stake", "solana_unstake").
 */
function isSolanaOperation(operation) {
    return typeof operation === "string" && operation.startsWith("solana_");
}
/**
 * Security module for validating unsigned transactions before signing.
 *
 * @remarks
 * The SecurityModule provides transaction integrity verification to protect against
 * unsigned transaction tampering. It wraps the `@blockdaemon/transaction-verifier` SDK
 * and delegates to protocol-specific modules for transaction verification.
 *
 * **Key Features:**
 * - **Transaction Verification**: Validate that encoded transactions match originating requests
 * - **Protocol-Specific Verifiers**: Built-in support for Stakewise and Solana operations
 * - **Automatic Routing**: Routes verification to appropriate module based on chain ID
 * - **Error Handling**: Typed errors with detailed mismatch information
 *
 * **Verification Flow:**
 * 1. **Build Transaction**: SDK module builds an unsigned transaction from request parameters
 * 2. **Verify Integrity**: SecurityModule routes to appropriate verifier based on chain/operation
 * 3. **Throw on Mismatch**: If verification fails, throws `SecurityModuleParameterMismatchError`
 * 4. **Return Result**: If verification passes, returns `IVerificationResult` with decoded details
 *
 * @example
 * **Basic Usage with StakingModule**
 * ```typescript
 * import { BdSdk, isSecurityModuleParameterMismatch } from "@blockdaemon/bd-sdk";
 *
 * const sdk = new BdSdk({ bdApiKey: "your-api-key" });
 *
 * try {
 *   // StakingModule automatically verifies transactions
 *   const tx = await sdk.staking.buildStakeTx({
 *     assets: 1000000000000000000n,
 *     userAddress: "0x...",
 *     chainId: "eip155:1",
 *   });
 * } catch (error) {
 *   if (isSecurityModuleParameterMismatch(error)) {
 *     console.error("Transaction verification failed:", error.details);
 *   }
 * }
 * ```
 *
 * @example
 * **Using verifyIntegrity**
 * ```typescript
 * import { SecurityModule, StakewiseOperationsEnum } from "@blockdaemon/bd-sdk";
 *
 * const security = new SecurityModule();
 *
 * const result = security.verifyIntegrity({
 *   operation: StakewiseOperationsEnum.STAKE,
 *   request: { assets: 1n, userAddress: "0x...", chainId: "eip155:1" },
 *   encodedTx: { to: "0x...", data: "0x...", value: 1n },
 * });
 * ```
 *
 * @see {@link ISecurityModuleOptions} for configuration options
 * @see {@link IVerificationResult} for return type
 * @see {@link SecurityModuleParameterMismatchError} for error handling
 *
 * @public
 */
export class SecurityModule {
    logger;
    logLevel;
    decodingSdk;
    /** Stakewise-specific verification module */
    stakewiseModule;
    /** Solana-specific verification module */
    solanaModule;
    /**
     * Creates a new SecurityModule instance.
     *
     * @param options - Configuration options for the security module
     *
     * @example
     * ```typescript
     * // Default configuration (enabled)
     * const security = new SecurityModule();
     *
     * // With custom options
     * const security = new SecurityModule({
     *   logLevel: "DEBUG",
     *   logger: customLogger,
     * });
     * ```
     */
    constructor(options = {}) {
        this.logLevel = options.logLevel ?? "info";
        this.logger =
            options.logger ?? getLogger({ label: "bd-sdk-security-module", level: this.logLevel });
        const decodingSDKConfig = {
            logLevel: this.logLevel,
        };
        this.decodingSdk = new DecodingSDK(decodingSDKConfig);
        // Initialize protocol-specific modules
        this.stakewiseModule = new SecurityModuleStakewise({
            logger: this.logger,
            getDecodingSdk: () => this.getDecodingSdk(),
        });
        this.solanaModule = new SecurityModuleSolana({
            logger: this.logger,
            decodingSdk: this.decodingSdk,
        });
    }
    /**
     * Returns the DecodingSDK instance.
     * @returns DecodingSDK instance
     */
    getDecodingSdk() {
        return this.decodingSdk;
    }
    /**
     * Unified verification method that routes to the appropriate verifier.
     *
     * @remarks
     * This is the single entry point for all verification operations. It automatically
     * routes to the correct protocol-specific module based on the operation type and
     * chain ID, providing consistent error handling and logging.
     *
     * **Routing Logic:**
     * - Solana chains (`solana:*`) or Solana operations (`solana_*`) → SecurityModuleSolana
     * - EVM chains (`eip155:*`) → SecurityModuleStakewise
     * - Unknown chains → Verification skipped
     *
     * @param input - The verification request containing operation type, request, and transaction
     * @returns IVerificationResult if verification passes
     * @throws {SecurityModuleParameterMismatchError} if verification fails
     *
     * @example
     * ```typescript
     * // Verify Stakewise stake transaction
     * const result = security.verifyIntegrity({
     *   operation: StakewiseOperationsEnum.STAKE,
     *   request: { assets: 1n, userAddress: "0x...", chainId: "eip155:1" },
     *   encodedTx: { to: "0x...", data: "0x...", value: 1n },
     * });
     *
     * // Verify Solana stake transaction
     * const result = security.verifyIntegrity({
     *   operation: SolanaOperationsEnum.STAKE,
     *   request: { assets: 1000000000n, userAddress: "...", chainId: "solana:mainnet" },
     *   encodedTx: { to: "...", data: "base64...", value: 0n },
     * });
     *
     * // Verify generic transaction
     * const result = security.verifyIntegrity({
     *   operation: StakewiseOperationsEnum.GENERIC,
     *   request: { chainId: "eip155:1" },
     *   encodedTx: { to: "0x...", data: "0x...", value: 0n },
     *   options: { expectedTo: "0x...", allowedToAddresses: ["0x..."] },
     * });
     * ```
     *
     * @remarks
     * `chainId` on the request is used for routing only. Unsigned encoded
     * transactions do not embed a chain identifier; ensuring the transaction targets
     * the intended network is the application's responsibility (see
     * {@link SecurityModuleStakewise} and {@link SecurityModuleSolana}).
     */
    verifyIntegrity(input) {
        const chainId = getChainId(input.request);
        try {
            // Route based on operation type OR chain ID
            if (isSolanaOperation(input.operation) || isSolanaChain(chainId)) {
                return this.solanaModule.routeOperation(input);
            }
            if (isEvmChain(chainId)) {
                return this.stakewiseModule.routeOperation(input);
            }
            this.logger.error(`Skipping security verification for unknown chain: ${chainId ?? "unknown"}`);
            throw throwSecurityModuleMismatchError(input.operation, { chainId: chainId ?? "unknown" }, `Skipping security verification for unknown chain: ${chainId ?? "unknown"}`);
        }
        catch (error) {
            if (isSecurityModuleParameterMismatch(error)) {
                this.logger.error("Security module parameter mismatch", {
                    operation: input.operation,
                    message: error.message,
                    details: error.details,
                });
            }
            throw error;
        }
    }
    /**
     * Verifies a Stakewise stake transaction against the originating request.
     *
     * @remarks
     * Unsigned EVM payloads do not carry a chain ID; this module does not verify
     * `request.chainId` against `encodedTx`. Ensuring the intended network is the
     * application's responsibility.
     *
     * @param request - The original stake request parameters
     * @param encodedTx - The unsigned transaction to verify
     * @param options - Optional verification options
     * @returns IVerificationResult with decoded transaction details
     * @throws {SecurityModuleStakeParameterMismatchError} if verification fails
     */
    verifyStakeUnsignedTx(request, encodedTx, options = {}) {
        return this.stakewiseModule.verifyStakeUnsignedTx(request, encodedTx, options);
    }
    /**
     * Verifies a Stakewise withdraw transaction against the originating request.
     *
     * @remarks
     * Unsigned EVM payloads do not carry a chain ID; this module does not verify
     * `request.chainId` against `encodedTx`. Ensuring the intended network is the
     * application's responsibility.
     *
     * @param request - The original withdraw request parameters
     * @param encodedTx - The unsigned transaction to verify
     * @param options - Optional verification options
     * @returns IVerificationResult with decoded transaction details
     * @throws {SecurityModuleWithdrawParameterMismatchError} if verification fails
     */
    verifyWithdrawUnsignedTx(request, encodedTx, options = {}) {
        return this.stakewiseModule.verifyWithdrawUnsignedTx(request, encodedTx, options);
    }
    /**
     * Verifies a Stakewise claim exit queue transaction against the originating request.
     *
     * @remarks
     * Unsigned EVM payloads do not carry a chain ID; this module does not verify
     * `request.chainId` against `encodedTx`. Ensuring the intended network is the
     * application's responsibility.
     *
     * @param request - The original claim request parameters
     * @param encodedTx - The unsigned transaction to verify
     * @param options - Optional verification options
     * @returns IVerificationResult with decoded transaction details
     * @throws {SecurityModuleClaimExitQueueParameterMismatchError} if verification fails
     */
    verifyClaimExitQueueUnsignedTx(request, encodedTx, options = {}) {
        return this.stakewiseModule.verifyClaimExitQueueUnsignedTx(request, encodedTx, options);
    }
    /**
     * Verifies a generic unsigned transaction against expected values.
     *
     * @remarks
     * Unsigned EVM payloads do not carry a chain ID; network must be enforced by
     * the application (e.g. options and signing context).
     *
     * @param encodedTx - The unsigned transaction to verify
     * @param options - Verification options including ABI, expected values, and allowlists
     * @returns IVerificationResult with decoded transaction details
     */
    verifyGenericUnsignedTx(encodedTx, options = {}) {
        return this.stakewiseModule.verifyGenericUnsignedTx(encodedTx, options);
    }
    // ===== Solana verification methods (delegated) =================================
    /**
     * Verifies a Solana stake transaction against the originating request.
     *
     * @remarks
     * Serialized Solana transaction bytes do not embed a CAIP-2 chain identifier;
     * this module does not verify `request.chainId` against the payload. Ensuring
     * the intended cluster is the application's responsibility.
     *
     * @param request - The original Solana stake request parameters
     * @param encodedTx - The unsigned Solana transaction to verify
     * @param options - Optional verification options
     * @returns IVerificationResult with decoded transaction details
     */
    verifySolanaStakeUnsignedTx(request, encodedTx, options = {}) {
        return this.solanaModule.verifySolanaStakeUnsignedTx(request, encodedTx, options);
    }
}
//# sourceMappingURL=data:application/json;base64,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